// PAY.JP のカード決済。
//
// ■ カード番号はこちらのサーバを通らない
// payjp.js（PAY.JP 公式、v2）のカード入力欄でトークンを作り、サーバへはトークンだけ渡す。
//
// ■ 金額・3Dセキュアはサーバが決める
// サーバが申込から金額を決めて支払いを作り、3Dセキュアの画面の URL を返す。
// 会員はそこで本人認証をして、イベントの画面（?pay=payjp&charge=…）へ戻ってくる。
// 戻ってきた後の確定は EventDetail が行う（finishPayjpReturn）。
import { useEffect, useRef, useState } from "react";
import { api } from "../api";

interface PayjpCardElement {
  mount: (el: HTMLElement | string) => void;
  unmount?: () => void;
}
interface PayjpClient {
  elements: () => { create: (type: "card") => PayjpCardElement };
  createToken: (el: PayjpCardElement) => Promise<{ id?: string; error?: { message?: string } }>;
}
declare global {
  interface Window {
    Payjp?: (publicKey: string) => PayjpClient;
  }
}

const PAYJP_JS_URL = "https://js.pay.jp/v2/pay.js";
/** 3Dセキュアの画面は PAY.JP のものだけ（それ以外の宛先には移さない） */
const ALLOWED_TDS = /^https:\/\/api\.pay\.jp\/v1\/tds\//;

let sdkLoad: Promise<void> | null = null;
function loadSdk(): Promise<void> {
  if (window.Payjp) return Promise.resolve();
  if (sdkLoad) return sdkLoad;
  sdkLoad = new Promise<void>((resolve, reject) => {
    const el = document.createElement("script");
    el.src = PAYJP_JS_URL;
    el.async = true;
    el.onload = () => resolve();
    el.onerror = () => {
      sdkLoad = null; // 次に開いたときにやり直せるように
      reject(new Error("PAY.JP を読み込めませんでした"));
    };
    document.head.appendChild(el);
  });
  return sdkLoad;
}

export function PayjpPayButton({ applicationId }: { applicationId: string }) {
  const box = useRef<HTMLDivElement>(null);
  const client = useRef<{ payjp: PayjpClient; card: PayjpCardElement } | null>(null);
  const [open, setOpen] = useState(false);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      try {
        const cfg = await api.get<{ publicKey: string }>(`/payments/payjp/config/${applicationId}`);
        await loadSdk();
        if (!alive || !box.current || !window.Payjp) return;
        const payjp = window.Payjp(cfg.publicKey);
        const card = payjp.elements().create("card");
        card.mount(box.current);
        client.current = { payjp, card };
        setReady(true);
      } catch (e) {
        if (alive) setErr((e as Error).message);
      }
    })();
    return () => {
      alive = false;
      client.current?.card.unmount?.();
      client.current = null;
      setReady(false);
    };
  }, [open, applicationId]);

  const pay = async () => {
    if (!client.current) return;
    setBusy(true);
    setErr(null);
    try {
      const t = await client.current.payjp.createToken(client.current.card);
      if (!t.id) throw new Error(t.error?.message ?? "カード情報を確認できませんでした");
      const r = await api.post<{ next: "three_d_secure" | "done"; url?: string }>("/payments/payjp/charges", {
        applicationId,
        token: t.id,
      });
      if (r.next === "three_d_secure") {
        if (!r.url || !ALLOWED_TDS.test(r.url)) throw new Error("本人認証の画面の宛先が想定外です");
        // 本人認証（3Dセキュア）の画面へ。戻ってきたら確定する
        window.location.assign(r.url);
        return; // 画面が切り替わるまでボタンは押せないまま（二重送信を防ぐ）
      }
      window.location.reload();
    } catch (e) {
      setErr((e as Error).message);
      setBusy(false);
    }
  };

  if (!open)
    return (
      <button type="button" className="btn-outline" onClick={() => setOpen(true)}>
        クレジットカードで支払う（PAY.JP）
      </button>
    );

  return (
    <div className="space-y-2 max-w-sm">
      <p className="text-sm font-semibold">クレジットカードで支払う（PAY.JP）</p>
      <div ref={box} className="input py-3" />
      {!ready && !err && <p className="text-xs text-tora-ink/60">カード入力欄を準備中…</p>}
      <button type="button" className="btn-primary" disabled={!ready || busy} onClick={pay}>
        {busy ? "処理中…" : "本人認証へ進む"}
      </button>
      <p className="text-xs text-tora-ink/60">
        次の画面でカード会社の本人認証（3Dセキュア）を行います。カード番号はこのサイトには送られません。
      </p>
      {err && <p className="text-sm text-red-700">{err}</p>}
    </div>
  );
}

/**
 * 3Dセキュアから戻ってきたときの確定。結果の文言を返す（支払えたかどうかはサーバが決める）。
 *
 * `retry` … 通信の途中切れ・サーバの一時的な障害で、結果がまだ分からない。
 * 支払いの ID を手放さず、もう一度確定を頼めるようにする（確定は何度頼んでも結果は同じ）。
 */
export async function finishPayjpReturn(
  chargeId: string,
): Promise<{ ok: boolean; message: string; retry?: boolean }> {
  try {
    const r = await api.post<{ status: string }>(
      `/payments/payjp/charges/${encodeURIComponent(chargeId)}/finish`,
      {},
    );
    return r.status === "succeeded"
      ? { ok: true, message: "お支払いが完了しました。" }
      : { ok: true, message: "お支払いを受け付けました。反映まで少しかかることがあります。" };
  } catch (e) {
    const code = (e as Error & { code?: string }).code;
    if (code === "already_paid") return { ok: true, message: "すでにお支払い済みです。" };
    const status = (e as Error & { status?: number }).status;
    if (status === undefined || status >= 500)
      return {
        ok: false,
        retry: true,
        message: "お支払いの結果を確認できませんでした。通信の状態を確かめて「もう一度確認する」を押してください。",
      };
    return { ok: false, message: (e as Error).message };
  }
}
